import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Eye, EyeOff, Trash2, Plus, X } from "lucide-react";

const CATEGORY_LABELS_ES = {
  food_cost: "Food Cost", groceries: "Alimentación", carnes: "Carnes",
  pescado_marisco: "Pescado y marisco", vinos: "Vinos", cerveza: "Cerveza",
  refrescos: "Refrescos", licores: "Licores", lacteos: "Lácteos",
  aceites: "Aceites", consumibles: "Consumibles", limpieza: "Limpieza",
  huevos: "Huevos", otros: "Otros",
};
const catLabel = (c) => CATEGORY_LABELS_ES[c] || (c || "otros");

/**
 * Manages the tenant's inventory catalog: hide/show products, add manual
 * entries, and bulk-hide/delete via checkbox column with shift-range select
 * plus the Delete key on the keyboard.
 */
export default function CatalogManagerDialog({ open, onClose, lang, onChanged }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHidden, setShowHidden] = useState(true);
  const [q, setQ] = useState("");
  const [manual, setManual] = useState({ name: "", category: "otros", subcategory: "", unit: "ud", unit_price: 0 });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const lastClickedRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventories/catalog", { params: { include_hidden: 1 } });
      setProducts(data.products || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { load(); setSelected(new Set()); lastClickedRef.current = null; } }, [open, load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return products.filter((p) => {
      if (!showHidden && p.hidden) return false;
      if (!s) return true;
      return (p.name || "").toLowerCase().includes(s) || (p.category || "").toLowerCase().includes(s);
    });
  }, [products, q, showHidden]);

  const grouped = useMemo(() => {
    const out = {};
    for (const p of filtered) {
      const cat = p.category || "otros";
      const sub = p.subcategory || "—";
      if (!out[cat]) out[cat] = {};
      if (!out[cat][sub]) out[cat][sub] = [];
      out[cat][sub].push(p);
    }
    return out;
  }, [filtered]);

  // Flat order (matches the DOM order) — used for shift-range selection.
  const flatOrder = useMemo(() => {
    const list = [];
    Object.entries(grouped).sort().forEach(([, subs]) => {
      Object.entries(subs).sort().forEach(([, items]) => items.forEach((p) => list.push(p.key)));
    });
    return list;
  }, [grouped]);

  const toggleOne = (key, ev) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const anchor = lastClickedRef.current;
      if (ev?.shiftKey && anchor && flatOrder.includes(anchor)) {
        // Range select from anchor → key
        const i0 = flatOrder.indexOf(anchor);
        const i1 = flatOrder.indexOf(key);
        if (i0 !== -1 && i1 !== -1) {
          const [a, b] = i0 < i1 ? [i0, i1] : [i1, i0];
          for (let i = a; i <= b; i++) next.add(flatOrder[i]);
        } else if (i1 !== -1) {
          next.add(key);
        }
      } else {
        if (next.has(key)) next.delete(key); else next.add(key);
      }
      return next;
    });
    lastClickedRef.current = key;
  };

  const selectAllVisible = () => setSelected(new Set(flatOrder));
  const clearSelection = () => setSelected(new Set());

  const namesFromSelection = () => {
    const byKey = Object.fromEntries(products.map((p) => [p.key, p.name]));
    return [...selected].map((k) => byKey[k]).filter(Boolean);
  };

  const bulkHide = async () => {
    const names = namesFromSelection();
    if (names.length === 0) return;
    try {
      await api.post("/inventories/catalog/bulk-hide", { names });
      toast.success(lang === "es" ? `Ocultados ${names.length} productos` : `Hidden ${names.length} products`);
      clearSelection();
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkDelete = async () => {
    const names = namesFromSelection();
    if (names.length === 0) return;
    if (!window.confirm(lang === "es"
      ? `¿Eliminar ${names.length} productos del catálogo? No volverán a aparecer.`
      : `Delete ${names.length} products? They won't reappear.`)) return;
    try {
      const { data } = await api.post("/inventories/catalog/bulk-delete", { names });
      toast.success(lang === "es"
        ? `Eliminados ${data.deleted || 0}, ocultos ${data.hidden || 0}`
        : `Deleted ${data.deleted || 0}, hidden ${data.hidden || 0}`);
      clearSelection();
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Delete-key shortcut when the modal has focus and rows are selected.
  const onKeyDown = (e) => {
    if ((e.key === "Delete" || e.key === "Backspace") && selected.size > 0) {
      e.preventDefault();
      bulkDelete();
    } else if (e.key === "Escape") {
      clearSelection();
    }
  };

  const toggleHidden = async (p) => {
    try {
      await api.post("/inventories/catalog/hide", {
        name: p.name, category: p.category, subcategory: p.subcategory, unit: p.unit,
        hidden: !p.hidden,
      });
      toast.success(!p.hidden
        ? (lang === "es" ? "Ocultado del listado" : "Hidden")
        : (lang === "es" ? "Restaurado" : "Restored"));
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteEntry = async (p) => {
    if (!p.id) { return toggleHidden(p); }
    if (!window.confirm(lang === "es" ? "¿Borrar del catálogo?" : "Delete from catalog?")) return;
    try {
      await api.delete(`/inventories/catalog/${p.id}`);
      toast.success(lang === "es" ? "Borrado" : "Deleted");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addManual = async () => {
    if (!manual.name.trim()) return toast.error(lang === "es" ? "Nombre requerido" : "Name required");
    setSaving(true);
    try {
      await api.post("/inventories/catalog/manual", {
        name: manual.name.trim(),
        category: manual.category || "otros",
        subcategory: manual.subcategory || null,
        unit: manual.unit || "ud",
        unit_price: parseFloat(manual.unit_price) || 0,
      });
      toast.success(lang === "es" ? "Producto añadido" : "Product added");
      setManual({ name: "", category: "otros", subcategory: "", unit: "ud", unit_price: 0 });
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="catalog-manager-dialog"
        onKeyDown={onKeyDown}
      >
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Catálogo de inventario" : "Inventory catalog"}</DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-[var(--border-color)] p-3 bg-[var(--surface-hover)]/50">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2 text-[var(--text-secondary)]">
            {lang === "es" ? "Añadir producto manualmente" : "Add product manually"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase">{lang === "es" ? "Nombre" : "Name"}</Label>
              <Input value={manual.name} onChange={(e) => setManual((s) => ({ ...s, name: e.target.value }))} placeholder={lang === "es" ? "Ej. Aceite girasol 5L" : "e.g. Sunflower oil 5L"} data-testid="cat-manual-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">{lang === "es" ? "Categoría" : "Category"}</Label>
              <Input value={manual.category} onChange={(e) => setManual((s) => ({ ...s, category: e.target.value }))} placeholder="otros" data-testid="cat-manual-category" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">{lang === "es" ? "Subcategoría" : "Subcategory"}</Label>
              <Input value={manual.subcategory} onChange={(e) => setManual((s) => ({ ...s, subcategory: e.target.value }))} placeholder={lang === "es" ? "(opcional)" : "(optional)"} />
            </div>
            <div>
              <Label className="text-[10px] uppercase">{lang === "es" ? "Unidad" : "Unit"}</Label>
              <Input value={manual.unit} onChange={(e) => setManual((s) => ({ ...s, unit: e.target.value }))} placeholder="ud" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">{lang === "es" ? "Precio ud" : "Unit price"}</Label>
              <Input type="number" step="0.0001" value={manual.unit_price} onChange={(e) => setManual((s) => ({ ...s, unit_price: e.target.value }))} data-testid="cat-manual-price" />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={addManual} disabled={saving} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="cat-manual-add-btn">
              <Plus size={14} className="mr-1" /> {lang === "es" ? "Añadir" : "Add"}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Input
            className="flex-1 min-w-[180px]"
            placeholder={lang === "es" ? "Buscar producto o categoría…" : "Search product or category…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button size="sm" variant="outline" onClick={() => setShowHidden((v) => !v)}>
            {showHidden ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
            {showHidden
              ? (lang === "es" ? "Ocultar los ocultos" : "Hide the hidden")
              : (lang === "es" ? "Mostrar ocultos" : "Show hidden")}
          </Button>
        </div>

        {/* Bulk action bar */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-xs text-[var(--text-muted)]">
            {selected.size > 0
              ? (lang === "es" ? `${selected.size} seleccionados` : `${selected.size} selected`)
              : (lang === "es" ? "Marca varios con Shift+clic. Suprimir para eliminar." : "Shift+click to range-select. Del to delete.")}
          </span>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={selectAllVisible} data-testid="cat-select-all">
            {lang === "es" ? "Marcar todo" : "Select all"}
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection} disabled={selected.size === 0}>
            {lang === "es" ? "Limpiar" : "Clear"}
          </Button>
          <Button size="sm" variant="outline" onClick={bulkHide} disabled={selected.size === 0} data-testid="cat-bulk-hide-btn">
            <EyeOff size={14} className="mr-1" /> {lang === "es" ? "Ocultar seleccionados" : "Hide selected"}
          </Button>
          <Button size="sm" variant="outline" onClick={bulkDelete} disabled={selected.size === 0}
            className="border-[var(--status-negative)]/50 text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
            data-testid="cat-bulk-delete-btn"
          >
            <Trash2 size={14} className="mr-1" /> {lang === "es" ? "Eliminar seleccionados" : "Delete selected"}
          </Button>
        </div>

        <div className="mt-2 border border-[var(--border-color)] rounded-md max-h-[45vh] overflow-y-auto">
          {loading && <div className="p-4 text-center text-sm text-[var(--text-muted)]">…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-[var(--text-muted)]">
              {lang === "es" ? "No hay productos que coincidan." : "No products match."}
            </div>
          )}
          {!loading && Object.entries(grouped).sort().map(([cat, subs]) => (
            <div key={cat}>
              <div className="px-3 py-1.5 bg-[var(--surface-hover)] border-t border-[var(--border-color)] text-[11px] uppercase tracking-wider font-semibold">
                {catLabel(cat)}
              </div>
              {Object.entries(subs).sort().map(([sub, items]) => (
                <div key={sub}>
                  {sub !== "—" && (
                    <div className="px-6 py-1 bg-[var(--surface-hover)]/40 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                      {sub}
                    </div>
                  )}
                  {items.map((p) => {
                    const isChecked = selected.has(p.key);
                    return (
                      <div
                        key={`${cat}-${sub}-${p.key}`}
                        className={`flex items-center px-3 py-1.5 border-t border-[var(--border-color)]/40 text-xs gap-2 ${p.hidden ? "opacity-45" : ""} ${isChecked ? "bg-[var(--brand-accent)]/5" : ""}`}
                      >
                        <span
                          onClick={(e) => { e.stopPropagation(); toggleOne(p.key, e); }}
                          className="cursor-pointer select-none inline-flex items-center"
                        >
                          <Checkbox
                            checked={isChecked}
                            // onCheckedChange fires on both mouse + keyboard; the
                            // wrapping span already handles mouse clicks (+shift
                            // for range). Reserve this callback for keyboard-only.
                            onCheckedChange={() => {}}
                            data-testid={`cat-check-${p.key}`}
                          />
                        </span>
                        <span
                          className="flex-1 truncate cursor-pointer select-none"
                          onClick={(e) => toggleOne(p.key, e)}
                        >
                          {p.name}
                          {p.source === "manual" && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">manual</span>}
                          {p.source === "albaran" && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-sky-100 text-sky-800">albarán</span>}
                          {p.hidden && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-800">{lang === "es" ? "oculto" : "hidden"}</span>}
                        </span>
                        <span className="w-14 text-right font-mono-num text-[var(--text-muted)]">{p.unit}</span>
                        <span className="w-20 text-right font-mono-num">{Number(p.latest_unit_price || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: "always" })}</span>
                        <Button size="sm" variant="ghost" onClick={() => toggleHidden(p)} title={p.hidden ? (lang === "es" ? "Mostrar" : "Show") : (lang === "es" ? "Ocultar" : "Hide")} data-testid={`cat-hide-${p.key}`}>
                          {p.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                        </Button>
                        {p.id && p.source !== "invoice_overlay" && (
                          <Button size="sm" variant="ghost" onClick={() => deleteEntry(p)} className="text-[var(--status-negative)]" title={lang === "es" ? "Borrar" : "Delete"}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> {lang === "es" ? "Cerrar" : "Close"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
